import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, onSnapshot, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { addShare, deleteShare, updateShare } from '../../services/kurbanService';
import { canViewBranchData } from '../../utils/branchUtils';
import { db } from '../../firebase/config';
import { Users, Loader2, Search, CheckCircle, FileText, PlusCircle, AlertCircle, Wallet, Trash2, Edit2, Save, X, Tag, MessageCircle } from 'lucide-react';
import PaymentModal from '../../components/shared/PaymentModal';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export default function ShareholderManagement() {
  const { isSuperAdmin, canDeleteShareholder, userProfile, currentUser, systemSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // URL parametresi
  const searchParams = new URLSearchParams(location.search);
  const highlightShareId = searchParams.get('highlight');

  // Arama ve Filtre State
  const [searchQuery, setSearchQuery] = useState('');
  const [showMissingProxyOnly, setShowMissingProxyOnly] = useState(false);
  const [showMissingPaymentOnly, setShowMissingPaymentOnly] = useState(false);
  const [activeBranchFilter, setActiveBranchFilter] = useState('all');

  // Form States (Hisse Ekle)
  const [allAnimals, setAllAnimals] = useState([]);
  const [isAddingShare, setIsAddingShare] = useState(false);
  const [submitActionType, setSubmitActionType] = useState('save');
  const [shareFormError, setShareFormError] = useState('');
  
  // Inline Edit States
  const [editingShareId, setEditingShareId] = useState(null);
  const [editShareForm, setEditShareForm] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Form ve Detay işlemleri için
  const [shareForm, setShareForm] = useState({
    animalId: searchParams.get('preSelectAnimal') || '', customerFirstName: '', customerLastName: '',
    customerPhone: '', price: '', originalPrice: '', discountAmount: '0', isProxyGiven: false, shareWeight: 1
  });

  // Payment Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [openInTransfer, setOpenInTransfer] = useState(false);

  // Delete Share States
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [shareToDelete, setShareToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    // 1. Hayvanları Çek (Tüm hayvanları çekerek WhatsApp mesajı dahil bilgileri bulabilmek için)
    const systemBranches = systemSettings?.systemBranches || [];
    const animalQ = query(collection(db, 'animals'));
    const unSubAnimals = onSnapshot(animalQ, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filtered = isSuperAdmin ? data : data.filter(a => canViewBranchData(userProfile, a.branch, systemBranches));
      setAllAnimals(filtered);
    });

    // 2. Hissedarları Çek
    const shareQ = query(collection(db, 'shares'), orderBy('createdAt', 'desc'));
    const unSubShares = onSnapshot(shareQ, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setShares(data);
      setLoading(false);
    }, (error) => {
      console.error('Hissedarlar çekilemedi:', error);
      setLoading(false);
    });

    return () => {
      unSubAnimals();
      unSubShares();
    };
  }, [isSuperAdmin, userProfile]);

  useEffect(() => {
    // shares yüklendiğinde ve url'de openPayment parametresi olduğunda;
    const params = new URLSearchParams(location.search);
    if (shares.length > 0 && highlightShareId && params.get('openPayment') === 'true') {
       const sh = shares.find(s => s.id === highlightShareId);
       if (sh) {
          setSelectedShare(sh);
          setPaymentModalOpen(true);
       }
       // Burada window.history yerine dogrudan react-router fonksiyonunu kullanarak
       // React'in state'inden de bu degeri aninda ve gercekten dusuruyoruz. 
       // Bu sayede modal tekrar kapanmaya veya baska bir re-render'da acilmaya calisip takilmaz.
       navigate(`/dashboard/hissedarlar?highlight=${highlightShareId}`, { replace: true });
    }
  }, [shares.length, location.search, highlightShareId, navigate]);

  // URL'den önceden seçili hayvan geldiyse ve hayvan listesi yüklendiğinde otomatik fiyat hesabı
  useEffect(() => {
    if (shareForm.animalId && !shareForm.price && allAnimals.length > 0) {
      const selectedAnimal = allAnimals.find(a => a.id === shareForm.animalId);
      if (selectedAnimal && selectedAnimal.price) {
         const ts = Number(selectedAnimal.totalShares) || 7;
         const calculatedPrice = Math.round((Number(selectedAnimal.price) / ts) * (shareForm.shareWeight || 1)).toString();
         setShareForm(prev => ({ ...prev, originalPrice: calculatedPrice, price: calculatedPrice, discountAmount: '0' }));
      }
    }
  }, [allAnimals, shareForm.animalId, shareForm.price, shareForm.shareWeight]);

  const handleAddShare = async (e) => {
    e.preventDefault();
    setShareFormError('');
    setIsAddingShare(true);

    try {
      if (!shareForm.animalId) throw new Error('Lütfen kurban seçiniz.');
      if (!shareForm.customerFirstName.trim() || !shareForm.customerLastName.trim()) {
        throw new Error('Müşteri Ad-Soyad alanları zorunludur.');
      }
      if (!shareForm.customerPhone.trim()) throw new Error('Telefon numarası zorunludur.');

      const phone = shareForm.customerPhone.replace(/[^\d]/g, '');
      if (phone.startsWith('0') && phone.length !== 11) {
         throw new Error("Başında 0 (sıfır) olan numaralar tam olarak 11 hane olmalıdır.");
      }
      if (!phone.startsWith('0') && phone.length !== 10) {
         throw new Error("Başında 0 (sıfır) bulunmayan numaralar tam olarak 10 hane olmalıdır.");
      }

      await addShare(shareForm.animalId, {
        customerFirstName: shareForm.customerFirstName,
        customerLastName: shareForm.customerLastName,
        customerPhone: phone,
        shareWeight: Number(shareForm.shareWeight) || 1,
        price: Number(shareForm.price) || 0,
        originalPrice: Number(shareForm.originalPrice) || Number(shareForm.price) || 0,
        discountAmount: Number(shareForm.discountAmount) || 0,
        isProxyGiven: shareForm.isProxyGiven,
      }, currentUser.uid);

      if (submitActionType === 'save_and_message') {
        const animal = allAnimals.find(a => a.id === shareForm.animalId);
        const tagStr = animal ? animal.tagNo : 'Bilinmeyen';
        const slghrtStr = animal ? animal.slaughterNo : '-';
        const msg = `Sayın ${shareForm.customerFirstName} ${shareForm.customerLastName}, Hasımoğlu Çiftliği'ne hoş geldiniz!\n\n${tagStr} küpe numaralı ve ${slghrtStr} kesim sırasındaki kurbanımıza hissedar olarak başarıyla kaydedildiniz.\nToplam hisse bedeliniz ${Number(shareForm.price).toLocaleString('tr-TR')} ₺ olarak sisteme işlenmiştir.\n\nDurumunuzu anlık takip etmek için:\nhttps://hasimoglucifligi.com`;
        
        let waPhone = phone;
        if (waPhone.startsWith('0')) waPhone = waPhone.substring(1);
        if (!waPhone.startsWith('90')) waPhone = '90' + waPhone;
        
        window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
      }

      setShareForm({ animalId: '', customerFirstName: '', customerLastName: '', customerPhone: '', price: '', isProxyGiven: false, shareWeight: 1 });
      alert('Hissedar başarıyla kurbana atandı!');
    } catch (error) {
      setShareFormError(error.message);
    } finally {
      setIsAddingShare(false);
    }
  };

  const confirmDeleteShare = (share) => {
    setShareToDelete(share);
    setDeletePassword('');
    setDeleteError('');
    setDeletePromptOpen(true);
  };

  const executeDeleteShare = async () => {
    if (deletePassword !== "251533") {
      setDeleteError("Hatalı güvenlik şifresi!");
      return;
    }
    try {
      await deleteShare(shareToDelete.id, shareToDelete.animalId);
      setDeletePromptOpen(false);
      setShareToDelete(null);
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  const handleOpenPaymentModal = (share, transfer = false) => {
    setSelectedShare(share);
    setOpenInTransfer(transfer);
    setPaymentModalOpen(true);
  };

  const liveShare = shares.find(s => s?.id === selectedShare?.id) || selectedShare;

  const getBranchName = (branchId) => {
    return systemSettings?.systemBranches?.find(b => b.id === branchId)?.name || 'Bilinmeyen Şube';
  };

  const handleAnimalSelect = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setShareForm({ ...shareForm, animalId: '', price: '', originalPrice: '', discountAmount: '0', shareWeight: 1 });
      return;
    }
    const selectedAnimal = allAnimals.find(a => a.id === selectedId);
    let calculatedPrice = '';
    const weight = shareForm.shareWeight || 1;
    if (selectedAnimal && selectedAnimal.price) {
       const ts = Number(selectedAnimal.totalShares) || 7;
       calculatedPrice = Math.round((Number(selectedAnimal.price) / ts) * weight).toString();
    }
    setShareForm({ ...shareForm, animalId: selectedId, price: calculatedPrice, originalPrice: calculatedPrice, discountAmount: '0' });
  };

  const handleShareWeightChange = (e) => {
     let weight = parseInt(e.target.value, 10);
     if (isNaN(weight) || weight < 1) weight = 1;

     let calcPrice = shareForm.price;
     if (shareForm.animalId) {
        const animal = allAnimals.find(a => a.id === shareForm.animalId);
        if (animal && animal.price) {
           const ts = Number(animal.totalShares) || 7;
           calcPrice = Math.round((Number(animal.price) / ts) * weight).toString();
        }
     }
     setShareForm({ ...shareForm, shareWeight: weight, originalPrice: calcPrice, price: calcPrice, discountAmount: '0' });
  };

  const handleDiscountChange = (e) => {
    let discount = Number(e.target.value) || 0;
    if (discount < 0) discount = 0;
    const orig = Number(shareForm.originalPrice) || 0;
    let netPrice = orig - discount;
    if (netPrice < 0) netPrice = 0;
    setShareForm({ ...shareForm, discountAmount: discount.toString(), price: netPrice.toString() });
  };

  const handlePriceChange = (e) => {
    let netPrice = Number(e.target.value) || 0;
    if (netPrice < 0) netPrice = 0;
    const orig = Number(shareForm.originalPrice) || 0;
    let discount = orig - netPrice;
    if (discount < 0) discount = 0;
    setShareForm({ ...shareForm, price: netPrice.toString(), discountAmount: discount.toString() });
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^\d]/g, ''); // Sadece rakamlari aliyoruz
    // Kullanıcıya bir şeyin silindiğini hissettirmeden hatalı gönderim yapmasını engellemek için
    // Sessizce kesmek (slice) yerine serbest bırakıyoruz, kaydet'te kontrol edeceğiz. Yinede max uzunluk güvenlik amaçlı 11.
    setShareForm({ ...shareForm, customerPhone: val.slice(0, 11) });
  };

  const handleSaveEdit = async () => {
    if (!editShareForm || !editShareForm.customerFirstName || !editShareForm.customerPhone) {
      alert("Lütfen gerekli alanları (Ad, Telefon) doldurun.");
      return;
    }
    setIsSavingEdit(true);
    try {
       await updateShare(editingShareId, editShareForm);
       setEditingShareId(null);
       setEditShareForm(null);
    } catch (err) {
       console.error("Hata:", err);
       alert("Güncellenirken bir hata oluştu.");
    } finally {
       setIsSavingEdit(false);
    }
  };

  const filteredShares = shares.filter(share => {
    // 1. GÜVENLİK FİLTRESİ: Alt adminler yalnızca yetkili oldukları kurbanların hissedarlarını görebilir.
    const animal = allAnimals.find(a => a.id === share.animalId);
    if (!isSuperAdmin && !animal) return false;

    // Şube Filtresi
    if (activeBranchFilter !== 'all') {
       if (!animal || animal.branch !== activeBranchFilter) return false;
    }

    // Vekalet Filtresi
    if (showMissingProxyOnly && share.isProxyGiven) return false;

    // Ödeme Filtresi
    if (showMissingPaymentOnly) {
       const debt = (Number(share.price) || 0) - (Number(share.paidAmount) || 0);
       if (debt <= 0) return false; // Borcu 0 veya altındaysa gösterme
    }

    if (!searchQuery.trim()) return true;
    const term = searchQuery.toLowerCase().trim();
    const fullName = `${share.customerFirstName || ''} ${share.customerLastName || ''}`.toLowerCase();
    const phone = share.customerPhone || '';
    
    // Find animal to search by its TagNo (Küpe Numarası)
    const tagNo = animal?.tagNo?.toLowerCase() || '';

    return fullName.includes(term) || phone.includes(term) || tagNo.includes(term);
  }).sort((a, b) => {
    const debtA = (Number(a.price) || 0) - (Number(a.paidAmount) || 0);
    const debtB = (Number(b.price) || 0) - (Number(b.paidAmount) || 0);
    return debtB - debtA;
  });

  return (
    <div className="space-y-6">
      
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <Users className="w-6 h-6 text-forest-400" />
          Hissedar ve Müşteri Paneli
        </h2>
        <p className="text-sm text-navy-400 mt-1">
          Kurbanlara atanmış tüm hissedarları buradan filtreleyebilir ve yeni atama yapabilirsiniz.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* Sol Taraftaki Form (Hisse Ekleme) */}
        <div className="xl:col-span-1">
          <div className="glass rounded-2xl border border-navy-700/50 p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-forest-400" />
              Yeni Hissedar Ekle
            </h3>
            <form onSubmit={handleAddShare} className="space-y-4">
              {shareFormError && (
                <div className="flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-danger-500">{shareFormError}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-navy-200 mb-1">Kurban Seçimi (Sadece Aktifler)</label>
                <select
                  value={shareForm.animalId} onChange={handleAnimalSelect}
                  className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                >
                  <option value="">Seçiniz...</option>
                  {allAnimals.filter(a => a.status === 'active').map((animal) => (
                    <option key={animal.id} value={animal.id}>
                       {animal.branch?.toUpperCase()} | Sıra: {animal.slaughterNo || '-'} | Hayvan No: {animal.animalNo || '-'} | Küpe: {animal.tagNo} ({animal.shareCount}/{animal.totalShares})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-navy-200 mb-1">Müşteri Adı</label>
                  <input
                     type="text" value={shareForm.customerFirstName} onChange={(e) => setShareForm({...shareForm, customerFirstName: e.target.value})}
                     className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy-200 mb-1">Müşteri Soyadı</label>
                  <input
                     type="text" value={shareForm.customerLastName} onChange={(e) => setShareForm({...shareForm, customerLastName: e.target.value})}
                     className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-navy-200 mb-1">Telefon No</label>
                  <input
                     type="tel" placeholder="05554443322" value={shareForm.customerPhone} onChange={handlePhoneChange}
                     className="w-full px-3 py-2.5 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none text-sm font-mono tracking-widest"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1 border border-forest-500/30 bg-forest-900/10 px-3 py-1.5 rounded-lg">
                  <label className="block text-xs font-bold text-forest-400 mb-1">Alınacak Hisse Adedi</label>
                  <input
                     type="number" min="1" max={shareForm.animalId ? (allAnimals.find(a => a.id === shareForm.animalId)?.totalShares - allAnimals.find(a => a.id === shareForm.animalId)?.shareCount) : 7} 
                     value={shareForm.shareWeight} onChange={handleShareWeightChange}
                     className="w-full px-3 py-1.5 rounded-lg bg-navy-800 border border-forest-600/30 text-white focus:border-forest-500 outline-none text-sm font-bold text-center"
                     required
                  />
                </div>
              </div>

               <div className="grid grid-cols-2 gap-4">
                 <div className="bg-navy-900/40 p-3 rounded-lg border border-navy-700/50 flex flex-col justify-center relative overflow-hidden group">
                   <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:opacity-10 transition-opacity"><Tag className="w-8 h-8" /></div>
                   <label className="block text-xs font-medium text-navy-400 mb-1">Liste Fiyatı (Orijinal)</label>
                   <div className="text-white font-bold text-lg">{shareForm.originalPrice ? `${Number(shareForm.originalPrice).toLocaleString('tr-TR')} ₺` : '-'}</div>
                 </div>
                 <div className="bg-navy-900/40 p-3 rounded-lg border-b-2 border-b-warning-500/50">
                   <label className="block text-xs font-bold text-warning-400 mb-1 flex items-center gap-1">İndirim Tutarı ₺</label>
                   <input
                     type="number" value={shareForm.discountAmount} onChange={handleDiscountChange} min="0"
                     className="w-full px-3 py-2 rounded-lg bg-navy-800 border border-warning-600/30 text-warning-400 focus:border-warning-500 outline-none text-sm font-bold"
                   />
                 </div>
                 <div className="col-span-2 relative">
                   <label className="block text-xs font-bold text-emerald-400 mb-1">Müşterinin Ödeyeceği Net Tutar ₺ (Kesin Fiyat)</label>
                   <div className="absolute top-1 right-2 text-[10px] text-navy-400 font-medium">Bu rakam müşterinin borcudur</div>
                   <input
                     type="number" value={shareForm.price} onChange={handlePriceChange} min="0"
                     className="w-full px-4 py-3 rounded-xl bg-emerald-900/20 border-2 border-emerald-500/50 text-white focus:border-emerald-400 outline-none text-lg font-bold shadow-[0_0_15px_rgba(16,185,129,0.1)]"
                   />
                 </div>
               </div>

              {/* KRİTİK: Vekalet Checkbox (Opsiyonel ama Uyarıcı) */}
              <div className="flex flex-col gap-2 mt-2">
                 <div className="flex items-center gap-3 p-3 rounded-lg bg-navy-900/60 border border-navy-700">
                   <input
                     type="checkbox" id="vekalet"
                     checked={shareForm.isProxyGiven} onChange={(e) => setShareForm({...shareForm, isProxyGiven: e.target.checked})}
                     className="w-5 h-5 rounded border-navy-600 text-forest-600 focus:ring-forest-500 bg-navy-800"
                   />
                   <label htmlFor="vekalet" className="text-sm font-medium text-white cursor-pointer -select-none leading-tight">
                     Müşteriden Dini Kesim Vekaleti Alındı
                   </label>
                 </div>
                 <p className="text-[11px] text-gold-400/90 flex items-start gap-1 ml-1 leading-tight font-medium">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    Mecburi değildir ancak kesim gününden en geç 3 gün önce vekaletin mutlaka alınmış olması gereklidir!
                 </p>
              </div>

              <div className="flex gap-2 mt-4 w-full">
                <button
                  type="submit" disabled={isAddingShare} onClick={() => setSubmitActionType('save')}
                  className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-navy-700 to-navy-800 hover:from-navy-600 hover:to-navy-700 border border-navy-600 disabled:opacity-50 transition-all flex items-center justify-center gap-1.5 shadow-lg text-xs"
                >
                  {isAddingShare && submitActionType === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sadece Ekle'}
                </button>
                <button
                  type="submit" disabled={isAddingShare} onClick={() => setSubmitActionType('save_and_message')}
                  className="flex-[2] py-3 rounded-xl font-bold text-white bg-gradient-to-r from-forest-600 to-forest-700 hover:from-forest-500 hover:to-forest-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-forest-600/20 text-sm"
                >
                  {isAddingShare && submitActionType === 'save_and_message' ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                     <><div className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> <span>Ekle & WhatsApp At</span></div></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sağ Taraftaki Liste (Tablo) */}
        <div className="xl:col-span-2">
          <div className="glass rounded-2xl border border-navy-700/50 overflow-hidden shadow-xl">
            {isSuperAdmin && systemSettings?.systemBranches?.length > 0 && (
               <div className="px-5 py-3 border-b border-navy-700/50 bg-navy-900/80 flex flex-wrap gap-2 items-center">
                 <span className="text-xs font-bold text-navy-400 mr-2 uppercase tracking-tight">Şube Filtresi:</span>
                 <button 
                   onClick={() => setActiveBranchFilter('all')} 
                   className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeBranchFilter === 'all' ? 'bg-gold-500 text-navy-950 shadow-lg border border-gold-400' : 'bg-navy-800 text-gold-400 border border-gold-500/30 hover:bg-gold-500/20'}`}
                 >
                   TÜMÜ
                 </button>
                 {systemSettings.systemBranches.map(branch => (
                    <button 
                      key={branch.id} 
                      onClick={() => setActiveBranchFilter(branch.id)} 
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all uppercase ${activeBranchFilter === branch.id ? 'bg-forest-600 text-white shadow-lg border border-forest-500' : 'bg-navy-800 text-forest-400 border border-forest-500/30 hover:bg-forest-500/20'}`}
                    >
                      {branch.name}
                    </button>
                 ))}
               </div>
            )}
            <div className="px-5 py-4 border-b border-navy-700/50 bg-navy-900/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-72">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-navy-400"><Search className="w-4 h-4" /></span>
                  <input
                    type="text" placeholder="İsim, Telefon veya Küpe No / Kurban ID ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none text-sm"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only" 
                      checked={showMissingPaymentOnly} 
                      onChange={(e) => setShowMissingPaymentOnly(e.target.checked)} 
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${showMissingPaymentOnly ? 'bg-warning-500' : 'bg-navy-700 pointer-events-auto shadow-inner border border-navy-600'}`}></div>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showMissingPaymentOnly ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <span className="text-sm font-medium text-navy-300 group-hover:text-white transition-colors select-none">
                    Ödemesi EKSİK Olanlar
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only" 
                      checked={showMissingProxyOnly} 
                      onChange={(e) => setShowMissingProxyOnly(e.target.checked)} 
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${showMissingProxyOnly ? 'bg-danger-500' : 'bg-navy-700 pointer-events-auto shadow-inner border border-navy-600'}`}></div>
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showMissingProxyOnly ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <span className="text-sm font-medium text-navy-300 group-hover:text-white transition-colors select-none">
                    Vekaleti EKSİK Olanlar
                  </span>
                </label>
              </div>
              <div className="text-xs text-navy-300">
                Toplam: <span className="font-bold text-white ml-1">{filteredShares.length}</span> Kayıt
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-forest-500 animate-spin" /></div>
            ) : filteredShares.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-16 text-navy-400">
                <FileText className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-sm">Hiç hissedar kaydı bulunamadı.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-navy-900/80 border-b border-navy-700/50">
                    <tr>
                      <th className="px-5 py-3 font-semibold text-navy-300">Müşteri Bilgisi</th>
                      <th className="px-5 py-3 font-semibold text-navy-300">İletişim / Kurban ID</th>
                      <th className="px-5 py-3 font-semibold text-navy-300 text-right">Bakiye Durumu</th>
                      <th className="px-5 py-3 font-semibold text-navy-300 text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy-700/50">
                    {filteredShares.map(share => {
                      const isHighlighted = highlightShareId === share.id;
                      const price = share.price || 0;
                      const paid = share.paidAmount || 0;
                      const debt = price - paid;
                      
                      return (
                        <tr 
                          key={share.id} 
                          id={`share-${share.id}`}
                          className={`hover:bg-navy-800/40 transition-colors ${
                            isHighlighted ? 'bg-forest-900/30' : ''
                          }`}
                        >
                          <td className="px-5 py-3">
                            {editingShareId === share.id ? (
                               <div className="flex flex-col gap-1.5 w-48">
                                 <input type="text" value={editShareForm.customerFirstName} onChange={e => setEditShareForm({...editShareForm, customerFirstName: e.target.value})} className="w-full bg-navy-900 border border-gold-500 rounded px-2 py-1 text-xs text-white outline-none" placeholder="Ad" />
                                 <input type="text" value={editShareForm.customerLastName} onChange={e => setEditShareForm({...editShareForm, customerLastName: e.target.value})} className="w-full bg-navy-900 border border-gold-500 rounded px-2 py-1 text-xs text-white outline-none" placeholder="Soyad" />
                               </div>
                            ) : (
                               <div className="flex items-center gap-3 cursor-pointer group" onClick={() => handleOpenPaymentModal(share, false)}>
                                 <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-md border ${isHighlighted ? 'bg-forest-600 border-forest-500' : 'bg-gradient-to-br from-navy-600 to-navy-800 border-navy-600'} group-hover:shadow-[0_0_10px_rgba(34,197,94,0.3)] transition-all`}>
                                   {share.customerFirstName?.[0]?.toUpperCase()}{share.customerLastName?.[0]?.toUpperCase()}
                                 </div>
                                 <div className="group-hover:translate-x-1 transition-transform">
                                   <p className="font-bold text-white uppercase group-hover:text-forest-400 transition-colors">
                                     {share.customerFirstName} {share.customerLastName}
                                     {share.shareWeight > 1 && <span className="ml-2 text-[10px] bg-gold-500/20 text-gold-400 px-1.5 py-0.5 rounded-md border border-gold-500/30 shadow-sm">{share.shareWeight} HİSSE</span>}
                                   </p>
                                   {share.isProxyGiven && <span className="text-[9px] text-forest-400 bg-forest-500/10 px-1 py-0.5 rounded flex items-center w-max mt-0.5"><CheckCircle className="w-2.5 h-2.5 mr-0.5"/>Vekalet Alındı</span>}
                                 </div>
                               </div>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {editingShareId === share.id ? (
                               <div className="flex flex-col gap-1.5 w-36">
                                 <input type="text" value={editShareForm.customerPhone} onChange={e => setEditShareForm({...editShareForm, customerPhone: e.target.value.replace(/[^\d]/g, '').slice(0, 11)})} className="w-full bg-navy-900 border border-gold-500 rounded px-2 py-1 text-xs text-white font-mono outline-none" placeholder="05554443322" />
                                 <label className="flex items-center gap-1.5 text-xs text-white cursor-pointer mt-1 font-bold">
                                   <input type="checkbox" checked={editShareForm.isProxyGiven} onChange={e => setEditShareForm({...editShareForm, isProxyGiven: e.target.checked})} className="w-4 h-4 rounded text-forest-500 bg-navy-800 border-navy-600 focus:ring-0" />
                                   Vekalet Alındı
                                 </label>
                               </div>
                            ) : (
                               <>
                                 <span className="font-mono text-navy-300 block text-xs cursor-pointer hover:text-white transition-colors" onClick={() => handleOpenPaymentModal(share, false)}>{share.customerPhone}</span>
                                 <div className="flex flex-col gap-1 mt-1">
                                   {share.branch && (
                                     <span className="px-1.5 py-0.5 inline-block rounded bg-blue-900/20 border border-blue-500/30 text-blue-400 font-bold text-[9px] w-max uppercase">
                                       ŞUBE: {getBranchName(share.branch)}
                                     </span>
                                   )}
                                   {share.animalId === 'unassigned' ? (
                                     <span 
                                       onClick={(e) => { e.stopPropagation(); handleOpenPaymentModal(share, true); }}
                                       className="px-1.5 py-0.5 inline-block rounded bg-gold-900/30 border border-gold-500/30 text-gold-400 font-bold text-[10px] w-max cursor-pointer hover:bg-gold-500/20 shadow-lg animate-pulse hover:animate-none"
                                     >
                                       KURBAN: BOŞTA (Tıklayıp Ata)
                                     </span>
                                   ) : (
                                     <span className="px-1.5 py-0.5 inline-block rounded bg-navy-800 border border-navy-700 text-navy-300 font-mono text-[9px] w-max">
                                       {allAnimals.find(a => a.id === share.animalId) 
                                         ? `Sıra: ${allAnimals.find(a => a.id === share.animalId).slaughterNo || '-'} | Küpe: ${allAnimals.find(a => a.id === share.animalId).tagNo}` 
                                         : 'Durum Bilinmiyor'}
                                     </span>
                                   )}
                                 </div>
                               </>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right">
                            {editingShareId === share.id ? (
                               <div className="flex flex-col items-end gap-1.5 w-32 ml-auto">
                                 <div className="relative w-full">
                                   <input type="number" title="Net Fiyat" value={editShareForm.price} onChange={e => {
                                      let net = Number(e.target.value) || 0;
                                      let orig = Number(editShareForm.originalPrice) || Number(share.originalPrice) || 0;
                                      let disc = Math.max(orig - net, 0);
                                      setEditShareForm({...editShareForm, price: net, discountAmount: disc});
                                   }} className="w-full bg-emerald-900/30 border border-emerald-500 rounded pl-2 pr-6 py-1 text-xs text-emerald-400 font-bold outline-none text-right" placeholder="Net Fiyat" />
                                   <span className="absolute right-2 top-1 text-[10px] text-emerald-500 font-bold pointer-events-none">₺</span>
                                 </div>
                                 <div className="relative w-full">
                                   <input type="number" title="İndirim" value={editShareForm.discountAmount} onChange={e => {
                                      let disc = Number(e.target.value) || 0;
                                      let orig = Number(editShareForm.originalPrice) || Number(share.originalPrice) || 0;
                                      let net = Math.max(orig - disc, 0);
                                      setEditShareForm({...editShareForm, discountAmount: disc, price: net});
                                   }} className="w-full bg-warning-900/30 border border-warning-500 rounded pl-2 pr-6 py-1 text-xs text-warning-400 font-bold outline-none text-right" placeholder="İndirim" />
                                   <span className="absolute right-2 top-1 text-[10px] text-warning-500 font-bold pointer-events-none">İND</span>
                                 </div>
                               </div>
                            ) : (
                               <div className="flex flex-col items-end gap-1">
                                  <span className="font-bold text-white text-sm">{price.toLocaleString('tr-TR')} ₺</span>
                                  {share.discountAmount > 0 && <span className="text-[9px] font-bold text-warning-400 bg-warning-900/30 border border-warning-500/30 px-1 py-0.5 rounded shadow-sm">İndirim: {Number(share.discountAmount).toLocaleString('tr-TR')} ₺</span>}
                                  {debt === 0 && price > 0 ? (
                                     <span className="text-[10px] font-bold text-forest-400 bg-forest-900/50 px-2 py-0.5 rounded mt-0.5">TAMAMI ÖDENDİ</span>
                                  ) : paid > 0 ? (
                                     <span className="text-[10px] font-bold text-gold-400 bg-gold-900/30 px-2 py-0.5 rounded mt-0.5">KALAN: {debt.toLocaleString('tr-TR')} ₺</span>
                                  ) : (
                                     <span className="text-[10px] font-bold text-danger-400 bg-danger-900/30 px-2 py-0.5 rounded mt-0.5">BEKLİYOR: {price.toLocaleString('tr-TR')} ₺</span>
                                  )}
                               </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-center">
                            {editingShareId === share.id ? (
                               <div className="flex justify-center items-center gap-2">
                                 <button onClick={handleSaveEdit} disabled={isSavingEdit} className="p-2 rounded-lg bg-forest-600 text-white hover:bg-forest-500 disabled:opacity-50 transition-colors shadow-lg" title="Kaydet">
                                   {isSavingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                 </button>
                                 <button onClick={() => setEditingShareId(null)} disabled={isSavingEdit} className="p-2 rounded-lg bg-navy-700 text-white hover:bg-navy-600 disabled:opacity-50 transition-colors shadow-lg" title="İptal">
                                   <X className="w-4 h-4" />
                                 </button>
                               </div>
                            ) : (
                               <div className="flex justify-center items-center gap-2">
                                 <button onClick={() => { setEditingShareId(share.id); setEditShareForm(share); }} className="p-1.5 rounded bg-navy-700 text-navy-300 hover:text-white hover:bg-navy-600 transition-colors" title="Düzenle">
                                    <Edit2 className="w-4 h-4" />
                                 </button>
                                 <button
                                   onClick={() => handleOpenPaymentModal(share)}
                                   className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-navy-800 hover:bg-forest-900/80 hover:text-forest-400 border border-navy-600 hover:border-forest-500 rounded-lg transition-colors text-xs font-semibold text-white"
                                 >
                                   <Wallet className="w-3.5 h-3.5" />
                                   Tahsilat
                                 </button>
                                 {canDeleteShareholder && (
                                   <button 
                                     onClick={() => confirmDeleteShare(share)}
                                     title="Süper Admin: Hissedar Sil"
                                     className="p-1.5 rounded bg-danger-500/10 hover:bg-danger-500 text-danger-500 hover:text-white border border-danger-500/20 transition-colors"
                                   >
                                      <Trash2 className="w-4 h-4" />
                                   </button>
                                 )}
                               </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

      <PaymentModal 
         isOpen={paymentModalOpen} 
         onClose={() => setPaymentModalOpen(false)}
         liveShare={liveShare}
         allAnimals={allAnimals}
         isSuperAdmin={isSuperAdmin}
         currentUserUid={currentUser.uid}
         initialTransferMode={openInTransfer}
      />

      {/* ── ŞİFRELİ SİLME ONAY MODALI ── */}
      {deletePromptOpen && shareToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setDeletePromptOpen(false)} />
           <div className="relative w-full max-w-sm glass border border-danger-500/50 rounded-2xl overflow-hidden shadow-2xl p-6 bg-navy-900 animate-in zoom-in-95">
              <div className="flex flex-col items-center text-center">
                 <div className="w-12 h-12 rounded-full bg-danger-500/20 flex items-center justify-center mb-4 border border-danger-500/50">
                    <AlertCircle className="w-6 h-6 text-danger-500" />
                 </div>
                 <h3 className="text-lg font-bold text-white mb-2">Hissedar Kaydını Sil</h3>
                 <p className="text-xs text-navy-300 mb-6">
                    <span className="font-bold text-white uppercase">{shareToDelete.customerFirstName} {shareToDelete.customerLastName}</span> adlı hissedarı sistemden tamamen siliyorsunuz. Bu hisse boşa çıkacaktır. Lütfen Süper Admin şifresini girin.
                 </p>
                 
                 {deleteError && (
                    <div className="w-full bg-danger-500/10 border border-danger-500/20 text-danger-500 text-xs py-2 rounded mb-4">
                       {deleteError}
                    </div>
                 )}

                 <input 
                    type="password" 
                    placeholder="Şifreyi giriniz..."
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-navy-700 text-white focus:border-danger-500 outline-none mb-6 text-center text-lg tracking-widest"
                    autoFocus
                 />

                 <div className="flex gap-3 w-full">
                    <button 
                       onClick={() => setDeletePromptOpen(false)}
                       className="flex-1 py-2.5 rounded-lg bg-navy-800 text-navy-300 hover:text-white transition-colors text-sm font-semibold"
                    >
                       İptal
                    </button>
                    <button 
                       onClick={executeDeleteShare}
                       className="flex-1 py-2.5 rounded-lg bg-danger-600 hover:bg-danger-500 text-white transition-colors text-sm font-bold shadow-lg shadow-danger-500/20"
                    >
                       Kalıcı Olarak Sil
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
}
